import React, { ReactNode } from 'react';
import EarthGlobe from './EarthGlobe';
import FloatingParticles from './FloatingParticles';
import MysticalLogo from './MysticalLogo';
import { Sparkles, Zap, Shield } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';

interface Auth3DLayoutProps {
  children: ReactNode;
}

export default function Auth3DLayout({ children }: Auth3DLayoutProps) {
  const { t } = useTranslation();

  return (
    <div className='min-h-screen relative overflow-hidden bg-[#0a0a12]'>
      {/* Canvas Particles Overlay */}
      <FloatingParticles count={30} />

      {/* Floating Data Layers - CSS Background Elements */}
      <div className='fixed inset-0 z-0 pointer-events-none overflow-hidden'>
        {/* Data layer 1 */}
        <div
          className='absolute w-[800px] h-[800px] rounded-full opacity-10'
          style={{
            background: 'radial-gradient(circle, rgba(14, 165, 233, 0.15) 0%, transparent 70%)',
            top: '-200px',
            right: '-200px',
            animation: 'float 120s ease-in-out infinite',
          }}
        />

        {/* Data layer 2 */}
        <div
          className='absolute w-[600px] h-[600px] rounded-full opacity-10'
          style={{
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, transparent 70%)',
            bottom: '-150px',
            left: '-150px',
            animation: 'float 90s ease-in-out infinite reverse',
          }}
        />

        {/* Hexagonal pattern overlay */}
        <svg
          className='absolute inset-0 w-full h-full opacity-[0.03]'
          xmlns='http://www.w3.org/2000/svg'
        >
          <defs>
            <pattern id='hexagons' width='56' height='100' patternUnits='userSpaceOnUse'>
              <path
                d='M28 66L0 50L0 16L28 0L56 16L56 50L28 66L28 100'
                fill='none'
                stroke='currentColor'
                strokeWidth='1'
              />
            </pattern>
          </defs>
          <rect width='100%' height='100%' fill='url(#hexagons)' />
        </svg>
      </div>

      {/* Content - Two Column Layout */}
      <div className='relative z-10 min-h-screen flex'>
        {/* Language Switcher - Top Right */}
        <div className='absolute top-6 right-6 xl:top-8 xl:right-8 z-20'>
          <LanguageSwitcher />
        </div>

        {/* Left Column - Product Introduction */}
        <div className='hidden lg:flex lg:w-1/2 flex-col justify-center pl-16 xl:pl-24 pr-8 relative'>
          {/* Earth Globe background for left column */}
          <div className='absolute inset-0 z-0'>
            <EarthGlobe />
          </div>
          <div
            className='space-y-8 max-w-xl relative z-10'
            style={{ animation: 'fadeInLeft 2s cubic-bezier(0.4, 0, 0.2, 1)' }}
          >
            {/* Logo with mystical effects */}
            <div className='flex items-center gap-4'>
              <MysticalLogo />
            </div>

            {/* Title */}
            <div className='space-y-3'>
              <h1
                className='text-5xl xl:text-6xl font-bold text-white leading-tight'
                style={{
                  fontFamily: 'Orbitron, sans-serif',
                  textShadow: '0 2px 20px rgba(0,0,0,0.8)',
                }}
              >
                {t('brand.name')}
              </h1>
              <h2 className='text-3xl xl:text-4xl font-medium text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500'>
                {t('brand.tagline')}
              </h2>
            </div>

            {/* Description */}
            <p
              className='text-gray-400 text-lg leading-relaxed'
              style={{ textShadow: '0 1px 10px rgba(0,0,0,0.6)' }}
            >
              {t('brand.description')}
            </p>

            {/* Feature Badges */}
            <div className='flex flex-wrap gap-3 pt-4'>
              <FeatureBadge icon={Sparkles} text={t('features.smartChat')} />
              <FeatureBadge icon={Zap} text={t('features.fastAnalysis')} />
              <FeatureBadge icon={Shield} text={t('features.dataSecurity')} />
            </div>
          </div>
        </div>

        {/* Right Column - Login Form */}
        <div className='w-full lg:w-1/2 flex items-center justify-center p-6 lg:p-8 xl:p-12'>
          <div
            className='w-full max-w-md'
            style={{ animation: 'fadeInRight 2s cubic-bezier(0.4, 0, 0.2, 1) 0.5s both' }}
          >
            {children}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) rotate(0deg);
          }
          50% {
            transform: translate(30px, 30px) rotate(180deg);
          }
        }

        @keyframes glow {
          0%, 100% {
            box-shadow: 0 0 40px rgba(14, 165, 233, 0.3);
          }
          50% {
            box-shadow: 0 0 60px rgba(139, 92, 246, 0.5);
          }
        }

        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}

// Feature Badge Component
function FeatureBadge({ icon: Icon, text }: { icon: React.ElementType; text: string }) {
  return (
    <div className='flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300'>
      <Icon className='text-cyan-400' size={16} />
      <span className='text-sm text-white/90 font-medium'>{text}</span>
    </div>
  );
}
